/**
 * An asset shown big, over the drawer's files: a picture, the start of a text, or what it is
 * (a sound plays). Download, copy what a page writes to show it, close.
 */
import { Match, Show, Switch, createMemo, createResource, onCleanup, type JSX } from "solid-js";
import { useApp } from "../../app/context";
import { assetName, embedOf, formatSize } from "../../shared/assets";
import { toast } from "../../shared/toast";

/** How many characters of a text are shown. */
const TEXT_SHOWN = 20_000;

/** The previewed asset, if any. */
export function AssetPreview(): JSX.Element {
  const { assets } = useApp();
  const [content] = createResource(
    () => assets.previewed(),
    (shown) => assets.read(shown.source, shown.path),
  );
  const info = () => content()?.info;
  /** The address of a picture, sound or video to show (given back when another is shown). */
  const url = createMemo(() => {
    const c = content();
    if (!c || !(c.info.kind === "image" || c.info.mime.startsWith("audio/") || c.info.mime.startsWith("video/"))) return null;
    const address = URL.createObjectURL(new Blob([c.bytes as BlobPart], { type: c.info.mime }));
    onCleanup(() => URL.revokeObjectURL(address));
    return address;
  });
  const text = () => {
    const c = content();
    return c?.info.kind === "text" ? new TextDecoder().decode(c.bytes.subarray(0, TEXT_SHOWN * 4)).slice(0, TEXT_SHOWN) : null;
  };
  return (
    <Show when={assets.previewed()}>
      {(shown) => (
        <div class="apreview" id="asset-preview" data-preview={shown().path} role="dialog" aria-label={`Preview of ${assetName(shown().path)}`}>
          <div class="dhead">
            <strong>{assetName(shown().path)}</strong>
            <span class="muted small">
              {info()?.mime} · {formatSize(info()?.size ?? 0)}
            </span>
            <span class="sp" />
            <Show when={shown().source === "project"}>
              <button type="button" class="mini" onClick={() => void navigator.clipboard?.writeText(embedOf(shown().path)).then(() => toast(`Copied ${embedOf(shown().path)}: paste it in a page.`))}>
                Copy for a page
              </button>
            </Show>
            <button type="button" class="mini" id="asset-download" onClick={() => void assets.download(shown().source, shown().path)}>
              Download
            </button>
            <button type="button" class="mini" id="asset-preview-close" onClick={() => assets.preview(null)}>
              Close
            </button>
          </div>
          <div class="dbody apreview-body">
            <Switch fallback={<p class="pempty">{content.loading ? "Reading…" : "This file cannot be shown here: download it to open it."}</p>}>
              <Match when={info()?.kind === "image" && url()}>{(src) => <img src={src()} alt={assetName(shown().path)} id="asset-preview-image" />}</Match>
              <Match when={info()?.mime.startsWith("audio/") && url()}>{(src) => <audio controls src={src()} />}</Match>
              <Match when={info()?.mime.startsWith("video/") && url()}>{(src) => <video controls src={src()} />}</Match>
              <Match when={text() !== null}>
                <pre class="apreview-text" id="asset-preview-text">
                  {text()}
                </pre>
              </Match>
            </Switch>
          </div>
        </div>
      )}
    </Show>
  );
}
